import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  Text,
  ScrollView,
  StyleProp,
  ViewStyle,
} from 'react-native';
import { Searchbar, Card, Chip } from 'react-native-paper';
import { useFocusEffect } from '@react-navigation/native';
import type { HomeReview, ReviewItem } from '../types';
import { reviewService } from '../services/reviewService';
import { theme, spacing } from '../constants/theme';
import {
  hasHydratedReviewSearchInventory,
  searchReviewItemsLocally,
} from '../utils/reviewItemSearch';

interface SearchBarProps {
  reviewId: number;
  onItemSelect: (item: ReviewItem) => void;
  placeholder?: string;
  style?: StyleProp<ViewStyle>;
  testID?: string;
  useNavigationFocus?: boolean;
  inModal?: boolean;
  onResultsVisibilityChange?: (visible: boolean) => void;
  review?: HomeReview | null;
  additionalItems?: ReviewItem[];
}

export const SearchBar: React.FC<SearchBarProps> = ({
  reviewId,
  onItemSelect,
  placeholder = 'Search items...',
  style,
  testID,
  useNavigationFocus = true,
  inModal = false,
  onResultsVisibilityChange,
  review,
  additionalItems = [],
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ReviewItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchVersionRef = useRef(0);
  const hasHydratedInventory = useMemo(
    () => hasHydratedReviewSearchInventory(review),
    [review]
  );

  const cancelPendingSearch = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    searchVersionRef.current += 1;
  }, []);

  const searchItems = useCallback(async (query: string, searchVersion: number) => {
    if (!query.trim()) {
      if (searchVersion === searchVersionRef.current) {
        setSearchResults([]);
        setShowResults(false);
        setLoading(false);
        onResultsVisibilityChange?.(false);
      }
      return;
    }

    if (hasHydratedInventory) {
      if (searchVersion === searchVersionRef.current) {
        setSearchResults(searchReviewItemsLocally(review, query, additionalItems));
        setShowResults(true);
        setLoading(false);
        onResultsVisibilityChange?.(true);
      }
      return;
    }

    try {
      const results = await reviewService.searchReviewItems(query, reviewId);
      if (searchVersion === searchVersionRef.current) {
        setSearchResults(results);
        setShowResults(true);
        onResultsVisibilityChange?.(true);
      }
    } catch (error) {
      console.error('Search failed:', error);
      if (searchVersion === searchVersionRef.current) {
        setSearchResults([]);
        setShowResults(false);
        onResultsVisibilityChange?.(false);
      }
    } finally {
      if (searchVersion === searchVersionRef.current) {
        setLoading(false);
      }
    }
  }, [additionalItems, hasHydratedInventory, onResultsVisibilityChange, review, reviewId]);

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);

    cancelPendingSearch();
    setSearchResults([]);
    setShowResults(false);

    if (!query.trim()) {
      setLoading(false);
      onResultsVisibilityChange?.(false);
      return;
    }

    const searchVersion = searchVersionRef.current;
    setLoading(true);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      void searchItems(query, searchVersion);
    }, 300);
  }, [cancelPendingSearch, onResultsVisibilityChange, searchItems]);

  const handleItemPress = (item: ReviewItem) => {
    onItemSelect(item);
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
    onResultsVisibilityChange?.(false);
  };

  const clearSearch = () => {
    cancelPendingSearch();
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
    setLoading(false);
    onResultsVisibilityChange?.(false);
  };

  useEffect(() => () => cancelPendingSearch(), [cancelPendingSearch]);

  // Clear search when screen loses focus (only if navigation is available)
  if (useNavigationFocus) {
    useFocusEffect(
      useCallback(() => {
        return () => {
          clearSearch();
        };
      }, [])
    );
  }

  const renderSearchResult = ({ item }: { item: ReviewItem }) => (
    <TouchableOpacity
      testID={testID ? `${testID}-result-${item.id}` : undefined}
      style={styles.resultItem}
      onPress={() => handleItemPress(item)}
    >
      <View style={styles.resultContent}>
        <Text style={styles.itemName}>{item.name}</Text>
        {item.description && (
          <Text style={styles.itemDescription} numberOfLines={1}>
            {item.description}
          </Text>
        )}
        {item.categoryName && <View style={styles.resultMeta}>
          <Chip
            mode="outlined"
            compact
            style={styles.categoryChip}
            textStyle={styles.categoryChipText}
          >
            {item.categoryName}
          </Chip>
        </View>}
      </View>
    </TouchableOpacity>
  );

  return (
    <View style={[styles.container, style, inModal && styles.modalContainer]}>
      <Searchbar
        testID={testID}
        placeholder={placeholder}
        onChangeText={handleSearchChange}
        value={searchQuery}
        style={[styles.searchbar, inModal && styles.modalSearchbar]}
        inputStyle={styles.searchInput}
        iconColor={theme.colors.primary}
        onClearIconPress={clearSearch}
        loading={loading}
      />
      
      {showResults && (
        <View
          style={[styles.resultsContainer, inModal && styles.modalResultsContainer]}
          onStartShouldSetResponder={() => !inModal}
          onMoveShouldSetResponder={() => !inModal}
        >
          <Card style={styles.resultsCard}>
            {searchResults.length > 0 ? (
              <>
                <View style={styles.resultsHeader}>
                  <Text style={styles.resultsCount}>
                    {searchResults.length} item{searchResults.length !== 1 ? 's' : ''} found
                  </Text>
                </View>
{inModal ? (
                <FlatList
                  data={searchResults}
                  renderItem={renderSearchResult}
                  keyExtractor={(item) => item.id.toString()}
                  style={styles.modalResultsList}
                  keyboardShouldPersistTaps="handled"
                  showsVerticalScrollIndicator={true}
                  nestedScrollEnabled={true}
                  scrollEnabled={true}
                  bounces={false}
                />
              ) : (
                <ScrollView
                  style={styles.resultsList}
                  contentContainerStyle={styles.quickSearchContent}
                  showsVerticalScrollIndicator={true}
                  scrollEnabled={true}
                >
                  {searchResults.map((item) => (
                    <View key={item.id}>
                      {renderSearchResult({ item })}
                    </View>
                  ))}
                </ScrollView>
              )}
            </>
          ) : (
            <View style={styles.noResults}>
              <Text style={styles.noResultsText}>
                No items found for "{searchQuery}"
              </Text>
            </View>
          )}
          </Card>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    zIndex: 999,
  },
  searchbar: {
    backgroundColor: 'white',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.22,
    shadowRadius: 2.22,
  },
  searchInput: {
    fontSize: 16,
  },
  resultsContainer: {
    position: 'absolute',
    top: 56,
    left: 0,
    right: 0,
    maxHeight: 400,
    zIndex: 1000,
    elevation: 1000,
  },
  resultsCard: {
    backgroundColor: 'white',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    borderRadius: 8,
    overflow: 'visible',
  },
  resultsHeader: {
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
    backgroundColor: theme.colors.backgroundLight,
  },
  resultsCount: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    fontWeight: '500',
  },
  resultsList: {
    height: 340,
    flex: 0,
  },
  resultItem: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  resultContent: {
    flex: 1,
  },
  itemName: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text,
    marginBottom: 4,
  },
  itemDescription: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    marginBottom: 8,
  },
  resultMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 4,
  },
  categoryChip: {
    backgroundColor: theme.colors.primaryLight,
    minHeight: 28,
    paddingVertical: 4,
  },
  categoryChipText: {
    fontSize: 12,
    color: theme.colors.primary,
    lineHeight: 14,
  },
  noResults: {
    padding: 20,
    alignItems: 'center',
  },
  noResultsText: {
    fontSize: 16,
    color: theme.colors.textSecondary,
    textAlign: 'center',
  },
  modalResultsContainer: {
    position: 'relative',
    top: 8,
    maxHeight: 400,
    marginBottom: spacing.md,
  },
  modalResultsList: {
    maxHeight: 350,
  },
  modalContainer: {
    flex: 1,
    zIndex: 1,
  },
  modalSearchbar: {
    backgroundColor: 'white',
    elevation: 0,
  },
  quickSearchContent: {
    flexGrow: 1,
    paddingBottom: 8,
  },
});
